/**
 * exercise-edit-main.js
 */

import React from 'react';
import {
  Grid,
} from '@mui/material';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import useDebounce from 'utils/use-debounce';
import usePrevious from 'utils/use-previous';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import WorkMode from 'components/form-inputs/work-mode';
import Autocomplete from 'components/form-inputs/autocomplete';
import Checkbox from 'components/form-inputs/checkbox';


const patrolExerciseNames = {
  [Types.Patrol.Training.OBEDIENCE]: 'Obedience Exercise',
  [Types.Patrol.Training.AGILITY]: 'Agility Exercise',
  [Types.Patrol.Training.APPREHENSION]: 'Apprehension Exercise',
  [Types.Patrol.Training.HUMAN_SEARCH]: 'Area Search For Humans Exercise',
  [Types.Patrol.Training.EVIDENCE_SEARCH]: 'Evidence Search Exercise',
  [Types.Patrol.Training.BUILDING_SEARCH]: 'Building Search Exercise',
  [Types.Patrol.Training.TRACKING]: 'Tracking Exercise',
  [Types.Patrol.Training.OTHER]: 'Other Exercise',
};


const autoGeneratedNames = [
  'Detection Exercise',
  'Patrol Exercise',
  'Patrol Scenario Exercise',
  ...Object.values(patrolExerciseNames)
];


export const generateName = (workMode, patrolTypeIds) => {
  if (workMode === Types.WorkMode.DETECTION) {
    return 'Detection Exercise';
  } else if (patrolTypeIds?.length === 0) {
    return 'Patrol Exercise';
  } else if (patrolTypeIds?.length > 1) {
    return 'Patrol Scenario Exercise';
  } else if (patrolTypeIds?.length > 0) {
    return patrolExerciseNames[patrolTypeIds[0]];
  } else {
    return '';
  }
}


export default function ExerciseEditMain(props) {
  const {
    exerciseRecord,
    storedRecord,
    drawerState,
    translation,
    options,
  } = props;
  const { t } = useTranslation(translation);
  const { control, setValue, getValues } = useFormContext();
  const recordId = storedRecord.id;
  const workModeValue = useWatch({ control, name: 'workMode' });
  const prevWorkMode = usePrevious(workModeValue);
  const detectionOdorIds = useWatch({ control, name: 'detectionOdorIds' });
  const patrolTypeIds = useWatch({ control, name: 'patrolTypeIds' });
  const prevPatrolTypeIds = usePrevious(patrolTypeIds);
  const patrolTypeIdsString = JSON.stringify(patrolTypeIds);
  const prevPatrolTypeIdsString = JSON.stringify(prevPatrolTypeIds);
  const nameValue = useWatch({ control, name: 'name' });
  const debouncedNameValue = useDebounce(nameValue || '', 200);


  // try to auto-generate name
  React.useEffect(() => {
    if ((
        debouncedNameValue === ''
        || autoGeneratedNames.includes(debouncedNameValue)
      )
      && (
        (workModeValue !== prevWorkMode && prevWorkMode !== undefined)
        || (patrolTypeIdsString !== prevPatrolTypeIdsString)
      )
    ) {
      setValue('name', generateName(workModeValue, patrolTypeIds));
    }

  // using stringified patrol type ids instead of array of ids
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recordId,
    workModeValue, 
    prevWorkMode,
    patrolTypeIdsString,
    prevPatrolTypeIdsString,
    debouncedNameValue, 
    setValue, 
  ]);
  
  
  // when name changes, update exercise record state
  // so that menu item title changes
  React.useEffect(() => {
    if (nameValue) {
      exerciseRecord.set(p => ({
        ...p,
        name: debouncedNameValue
      }));
    }

  // exerciseRecord is setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNameValue]);


  // automatically set controlled negative value based on odor configuration
  React.useEffect(() => {
    if (workModeValue === Types.WorkMode.DETECTION && detectionOdorIds) {
      setValue(
        'isControlledNegative',
        Boolean(
          detectionOdorIds.length === 0 
          || (detectionOdorIds.length === 1 && detectionOdorIds[0] === Types.OdorPrimary.PROOFING)
        )
      );
    }

  }, [workModeValue, detectionOdorIds, setValue]);


  // update record state work mode and patrol types when changed so icon updates
  React.useEffect(() => {
    const exercise = getValues();
    if (exercise.patrol) {
      exerciseRecord.set(p => ({
        ...p,
        workMode: workModeValue,
        patrol: exercise.patrol
      }));
    }

  // exerciseRecord is setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workModeValue, getValues, JSON.stringify(patrolTypeIds)]);


  return (
    <>
      <EditorIconSection
        iconName='summary'
        helpProps={{
          translation,
          section: 'info'
        }}
      >

        {/* exercise type / work mode */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.type.helpTitle')}
          text={t('info.type.helpInfo')}
          style={{ marginBottom: 8 }}
        >
          <WorkMode disabled={storedRecord.fullStats?.complete > 0} />
        </HelpTextItem>

        {/* exercise name */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.name.helpTitle')}
          text={t('info.name.helpInfo')}
        >
          {focusHandlers => 
            <Grid container spacing={2}>
              <FormGridItem>
                <Autocomplete
                  name='name'
                  label={t('info.name.label')}
                  options={options.names}
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('info.name.errMsgEmpty'),
                    validate: value => value.trim() !== '' || t('info.name.errMsgEmpty'),
                    maxLength: {
                      value: 250,
                      message: t('info.name.errMsgLength')
                    }
                  }}
                />
              </FormGridItem>
            </Grid>}
        </HelpTextItem>

        {/* exercise monitor */}
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('info.monitor.helpTitle')}
          text={t('info.monitor.helpInfo')}
        >
          {focusHandlers =>
            <Grid container spacing={2}>
              <FormGridItem>
                <Autocomplete
                  name='monitor'
                  label={t('info.monitor.label')}
                  options={options.monitors.map(o => o.name)}
                  focusHandlers={focusHandlers}
                  rules={{
                    required: t('info.monitor.errMsgEmpty'),
                    validate: value => value.trim() !== '' || t('info.monitor.errMsgEmpty'),
                    maxLength: {
                      value: 100,
                      message: t('info.monitor.errMsgLength')
                    }
                  }}
                />
              </FormGridItem>
            </Grid>}
        </HelpTextItem>

        {/* additional detection options */}
        {workModeValue === Types.WorkMode.DETECTION &&
        <div style={{ display: 'none' }}>

          {/* controlled negative - hidden and automatically set */}
          <Checkbox
            name='isControlledNegative'
            label={t('info.controlledNegative.label')}
          />

        </div>}

      </EditorIconSection>
    </>
  );
}